import { useMemo, useState } from 'react';
import { Button, Tooltip } from '@ds';
import { AdminPageHeader, DataTable, NotificationBadge, SearchInputWithHistory } from '@ui';
import { BulkAssignmentSelectedCases } from './components/BulkAssignmentSelectedCases';
import { BulkAssignmentFilters } from './components/BulkAssignmentFilters';
import { renderBulkAssignmentCell } from './renderBulkAssignmentCell';
import { BulkAssignmentCase } from '@/api/apis/case';
import { useBulkAssignmentListQuery } from '@/queries/case';
import { useBulkAssignmentSelection } from './hooks/useBulkAssignmentSelection';
import { useStartBulkAssignment } from './hooks/useStartBulkAssignment';
import { useBulkAssignmentRun } from './hooks/useBulkAssignmentRun';
import { BULK_ASSIGNMENT_FILTERS } from './constants';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { BulkAssignmentInProgressModal } from './components/BulkAssignmentInProgressModal';
import { LastRequiredEmployeeWarning } from './components/LastRequiredEmployeeWarning';
import { getDisabledKeys, getTableColumns } from './utils';

export const BulkAssignment = () => {
  const [isPrimary, setIsPrimary] = useState(false);
  const {
    filters,
    updateFilters,
    clearFilters,
    data,
    isLoading,
    isFetching,
    page,
    itemsPerPage,
    searchText,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
    sortBy,
    filterQuery,
  } = useFilterQueryWithParams({
    filterList: BULK_ASSIGNMENT_FILTERS,
    query: useBulkAssignmentListQuery,
    defaultSortBy: { column: 'DonorORtime', direction: 'descending' },
  });

  const queryGroup = filterQuery.group;

  const disabledKeys = useMemo(() => getDisabledKeys(filters, data?.items), [filters, data]);
  const tableColumns = useMemo(() => getTableColumns(queryGroup), [queryGroup]);
  const renderCell = useMemo(() => renderBulkAssignmentCell(queryGroup), [queryGroup]);

  const {
    selectedCases,
    setSelectedCases,
    selectedKeysOnPage,
    handleCaseSelectionChange,
    handleRemove,
    selectedItems,
  } = useBulkAssignmentSelection({ data: data?.items ?? [], disabledKeys });

  const { isRunActive, processedCount, casesTotal, operations, relatedEmployees, startRun } = useBulkAssignmentRun();

  const { handleBulkAssign, isAssignRequestPending } = useStartBulkAssignment({
    selectedItems,
    filters,
    isPrimary,
    setSelectedCases,
    startRun,
  });

  const userFiltersSelected = !!(filters.employeeIdsToAssign.length || filters.employeeIdsToDetach.length);
  const bulkAssignDisabled = !userFiltersSelected || selectedItems.length === 0;

  return (
    <div className="flex flex-col place-items-stretch gap-9 p-6">
      <AdminPageHeader title="Bulk Actions: Cases">
        <NotificationBadge />
      </AdminPageHeader>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-row gap-4 items-center">
            <SearchInputWithHistory
              handleSearch={setSearchText}
              searchText={searchText}
              storageKey="bulkAssignmentHistory"
            />
            <BulkAssignmentFilters
              filters={filters}
              updateFilters={updateFilters}
              clearFilters={clearFilters}
              selectedCases={selectedCases}
              setSelectedCases={setSelectedCases}
              isPrimary={isPrimary}
              setIsPrimary={setIsPrimary}
            />
          </div>
          <Tooltip content="Select role, user to detach and/or to add" isDisabled={!bulkAssignDisabled}>
            <div>
              <Button
                size="sm"
                isLoading={isAssignRequestPending || isRunActive}
                isDisabled={bulkAssignDisabled}
                onPress={handleBulkAssign}
              >
                Bulk Update
              </Button>
            </div>
          </Tooltip>
        </div>
        <BulkAssignmentSelectedCases
          selectedItems={selectedItems}
          onRemove={handleRemove}
          onClear={() => setSelectedCases({})}
        />
        <LastRequiredEmployeeWarning filters={filters} results={data?.items || []} />
        <DataTable<BulkAssignmentCase>
          idKey="caseId"
          label="Bulk Assignment"
          columns={tableColumns}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          selectionMode="multiple"
          selectedKeys={selectedKeysOnPage}
          disabledKeys={disabledKeys}
          disallowEmptySelection={false}
          totalItems={data?.totalItems ?? 0}
          totalPages={data?.totalPages ?? 1}
          page={page}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
          sortBy={sortBy}
          onSelectionChange={handleCaseSelectionChange}
          renderCell={renderCell}
        />
        <BulkAssignmentInProgressModal
          isOpen={isRunActive}
          processedCount={processedCount}
          casesTotal={casesTotal}
          operations={operations}
          relatedEmployees={relatedEmployees}
        />
      </div>
    </div>
  );
};
